import React from "react";
import { useRouteMatch } from "react-router-dom";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";

import { Text } from "src/library/components/Text";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import MainContentWrapper from "src/core/shared/components/Layout/MainContentWrapper";
import { PageProps } from "src/pages/Router/routes";
import SelectedPaymentCard from "src/modules/payment/components/PaymentMethodCard/SelectedPaymentMethodCard";
import { Address } from "src/library/components/Address";
import { IPaymentCard } from "src/modules/payment/api/payment.api";
import { Layout } from "src/core/shared/components/Layout";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { Button } from "src/library/components/Button";
import { useDrop } from "src/core/drops/hooks";
import { IStore } from "src/core/shared/store";
import { useInvoiceDetails } from "src/core/accounting/hooks/useInvoiceDetails";
import { PayInvoiceSummary } from "src/core/accounting/components/PayInvoiceSummary";
import { useDropsApi } from "src/core/drops/api/drops.api";

interface InvoiceCheckoutPageProps {
  invoiceId: string;
}

const InvoiceCheckoutPage: React.FunctionComponent<PageProps<InvoiceCheckoutPageProps>> = ({ ...props }) => {
  const { terminology } = props;
  const [ selectedPaymentMethodCard, setSelectedPaymentMethodCard ] = React.useState<IPaymentCard | null>(null);
  const match = useRouteMatch<{ invoiceId?: string }>();
  const [ { drop } ] = useDrop();
  const invoiceId = React.useMemo(() => match.params.invoiceId ? parseInt(match.params.invoiceId) : undefined, [ match.params.invoiceId ]);
  const { masqueradeMode } = useMappedState(({ global }: IStore) => ({ masqueradeMode: global.masqueradeMode }));

  const {
    invoice,
    loading: isLoadingInvoice,
    error: invoiceError
  } = useInvoiceDetails(invoiceId);

  // Fetch branch details if invoice has branchId
  const [ getBranchRes, getBranchReq ] = useDropsApi("GET_DROP");

  React.useEffect(() => {
    if (invoice?.branchId && !getBranchRes.data) {
      getBranchReq(invoice.branchId);
    }
  }, [
    getBranchReq,
    getBranchRes.data,
    invoice?.branchId
  ]);

  const branch = getBranchRes.data;

  const isAllowedPayments = React.useMemo(() => {
    if (drop?.groupedSettings.require_payment_method?.value.required === false || masqueradeMode?.active) {
      return false;
    }

    return true;
  }, [ drop?.groupedSettings.require_payment_method?.value.required, masqueradeMode?.active ]);

  if (isLoadingInvoice) {
    return (
      <Layout {...props}>
        <MainContentWrapper>
          <LoadingSpinner type="circular" />
        </MainContentWrapper>
      </Layout>
    );
  }

  if (invoiceError || !invoice) {
    return (
      <Layout {...props}>
        <MainContentWrapper>
          <ErrorNotice
            type="error"
            message={typeof invoiceError === "string" ? invoiceError : "Invoice not found"}
          />
        </MainContentWrapper>
      </Layout>
    );
  }

  return (
    <Layout {...props}>
      <MainContentWrapper>
        <StyledLayoutWrapper>
          {!isAllowedPayments ? (
            <div>
              <ErrorNotice
                type="error"
                message="Your account is not set up to accept payment by credit or debit card. You will be invoiced at a later date."
              />

              <div>
                <Button onClick={() => props.history.push("/dashboard")}>
                  Back to dashboard
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="column pay-with-wrapper">
                <Text as="h2">
                  Pay With
                </Text>

                <SelectedPaymentCard
                  selectedPaymentMethodCard={selectedPaymentMethodCard}
                  setSelectedPaymentMethodCard={setSelectedPaymentMethodCard}
                  terminology={terminology}
                  defaultPaymentMethodId={branch?.defaultPaymentCardId}
                />

                <Text as="h2">
                  Delivery Address
                </Text>

                {branch ? (
                  <>
                    <div className="address-row">
                      <Text>
                        {`Delivery Address code ${branch.branchNumber}`}
                      </Text>
                    </div>

                    <div className="branch-name">
                      <Text as="h4">
                        {branch.name}
                      </Text>
                    </div>

                    <div className="address">
                      <Address address={branch.address} />
                    </div>
                  </>
                ) : (
                  <Text color="secondary">
                    No delivery address available
                  </Text>
                )}
              </div>

              <div className="column order-summary">
                <PayInvoiceSummary
                  invoice={invoice}
                  selectedPaymentCard={selectedPaymentMethodCard}
                />
              </div>
            </>
          )}
        </StyledLayoutWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

const StyledLayoutWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;

    .order-summary {
      margin-top: ${theme.spacing.margins.lg};
      max-width: 390px;
    }

    .branch-name > :nth-child(1) {
      margin: ${theme.spacing.margins.xs} 0;
    }

    .address {
      font-size: ${theme.fonts.sizes.p};
      
      span {
        margin: ${theme.spacing.margins.xs} 0;
      }
    }

    @media (min-width: 1000px) {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      width: 100%;
      margin-top: 0;

      .column {
        display: flex;
        flex-direction: column;
        flex-basis: 100%;
      }
  
      .pay-with-wrapper {
        flex: 1;
      }
  
      .order-summary {
        flex: 1;
        flex-wrap: wrap;
        align-content: flex-end;
      }
    }
  `}
`;

export default InvoiceCheckoutPage;
